// Which hero layout, menu style, header and footer a page renders, from the
// theme's defaults and the business's choices (web_config.site, owned by D1;
// every read here is defensive). Pure, so tests run it under plain node.
import {canonicalTheme, themeRecord} from "./tokens.js";

export const HERO_LAYOUTS = ["full", "split", "framed", "words", "collage"];
export const NAV_VARIANTS = ["logo-left", "centred", "split", "minimal", "pill"];
export const MENU_STYLES = ["columns", "cards", "side", "dropdown"];
export const FOOTER_KINDS = ["cta", "local", "minimal", "columns"];

// A section's chosen layout if the theme allows it, else the theme's default.
// Flat only ever has its one hero.
export function heroLayout(themeName, layout) {
    const t = themeRecord(themeName);
    if (typeof layout === "string" && t.hero.allowed.includes(layout)) return layout;
    return t.hero.layout;
}

// The layout a hero actually draws. A theme whose own default is "words" (big
// type, no photo slot) would hide a photo the owner has just chosen, so with
// no layout picked by the owner (layout is "") and a photo or video in the
// section, the first allowed layout that shows a photo is used instead. An
// explicit "words" (Big words only) is always respected.
export function heroLayoutFor(themeName, layout, hasMedia = false) {
    const chosen = heroLayout(themeName, layout);
    if (chosen !== "words" || !hasMedia) return chosen;
    if (typeof layout === "string" && layout === "words" && themeRecord(themeName).hero.allowed.includes("words")) return chosen;
    return themeRecord(themeName).hero.allowed.find((l) => l !== "words") || chosen;
}

// Legacy themes keep their own hero unless a section asks for another layout
// the theme allows (Night Watch and Glass gain split/words/framed).
export function usesLegacyHero(themeName, layout) {
    const t = themeRecord(themeName);
    if (!t.legacy) return false;
    return heroLayout(themeName, layout) === "full";
}

const site = (pageData) => (pageData && typeof pageData.site === "object" && pageData.site) || {};

// The header: new themes use the new header in the theme's variant; legacy
// themes keep today's bar.
export function navVariant(themeName, pageData) {
    const t = themeRecord(themeName);
    if (t.legacy) return "classic";
    const chosen = site(pageData).nav?.variant;
    return NAV_VARIANTS.includes(chosen) ? chosen : t.nav.variant;
}

// Menu label: renameable ("Services", "Our work", "Shop"); "Explore" by default.
export function menuLabel(pageData) {
    const label = site(pageData).menu?.label;
    return typeof label === "string" && label.trim() ? label.trim().slice(0, 30) : "Explore";
}

// Menu style: the business's pick, else chosen by how much there is (S_MegaNav):
// one group or under 6 pages → simple dropdown; 5+ groups → side list;
// groups with photos → photo cards; otherwise columns + featured.
export function menuStyle(pageData, groups = []) {
    const chosen = site(pageData).menu?.style;
    if (MENU_STYLES.includes(chosen)) return chosen;
    const list = Array.isArray(groups) ? groups.filter((g) => g && Array.isArray(g.links) && g.links.length) : [];
    const pages = list.reduce((n, g) => n + g.links.length, 0);
    if (list.length <= 1 || pages < 6) return "dropdown";
    if (list.length >= 5) return "side";
    if (list.every((g) => g.image)) return "cards";
    return "columns";
}

// Up to six pages per group, then "All …" (no "See more" cut-off).
export const MENU_PAGES_PER_GROUP = 6;

// The footer: the business's kind, else the theme's; legacy themes keep
// today's footer until a kind is chosen.
export function footerKind(themeName, pageData) {
    const chosen = site(pageData).footer?.kind;
    if (FOOTER_KINDS.includes(chosen) && canonicalTheme(themeName) !== "flat") return chosen;
    const t = themeRecord(themeName);
    return t.legacy ? "classic" : t.footer.kind;
}
